import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PartsManagementService } from '../../../services/parts-management.service';
import { ReplaySubject, forkJoin, take, takeUntil, firstValueFrom, first } from 'rxjs';

import { RouterModule } from '@angular/router';
import { PartsManagementFilterForm } from '../../../form-model/parts-management-filter.form.model';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-input-date';
import { AuthPolicies } from '../../../auth/auth-policies';
import { utils } from '../../../helper/utils';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
import { PartsManagementFilter } from '../../../models/parts-management-filter.model';
import { PartsManagementHeader } from '../../../models/parts-management-header.model';
import { CodeDescription } from '../../../models/code-description.model';

@Component({
  selector: 'app-parts-management-advance-filter',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, AemsMultilineTagInputComponent],
  templateUrl: './parts-management-advance-filter.component.html',
  styleUrl: './parts-management-advance-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PartsManagementAdvanceFilterComponent implements OnInit {
  @Input({ required: true }) partsManagementFilterForm!: FormGroup<PartsManagementFilterForm>;
  @Input() isPartsManagementList: boolean = true;
  @Input() bindMultiSelects: boolean = false;
  partsManagementData: PartsManagementHeader[] = [];

  partManufacturerCodeList: CodeDescription[] = [];
  partGroupCodeList: CodeDescription[] = [];

  partnumberRegex: RegExp = /^[A-Za-z0-9\-\/]+$/;
  descriptionRegex: RegExp = /^[A-Za-z0-9,.*\-\/ ]+$/;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private partsManagementService: PartsManagementService,
    private formBuilder: FormBuilder,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit() {
    this.addControlsToTheFormGroup();
    this.loadAllPartsManagementUniqueOptions();
  }

  private addControlsToTheFormGroup() {
    this.partsManagementFilterForm.addControl('partCodesList', this.formBuilder.control<string[]>([]));
    this.partsManagementFilterForm.addControl('partGroupCodeList', this.formBuilder.control<string[]>([]));
    this.partsManagementFilterForm.addControl('partManufacturerCodeList', this.formBuilder.control<string[]>([]));
    this.partsManagementFilterForm.addControl('description', this.formBuilder.control<string[]>([]));
    this.partsManagementFilterForm.addControl('partActiveStatusesList', this.formBuilder.control<string[]>([]));
    this.partsManagementFilterForm.addControl('isSerialTagNumberRequired', this.formBuilder.control<string[]>([]));
    this.partsManagementFilterForm.addControl('deductCore', this.formBuilder.control<string[]>([]));
    this.partsManagementFilterForm.addControl('applyPMFilter', this.formBuilder.control<boolean | null>(null));
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  isValueSelected(controlName: 'partActiveStatusesList' | 'isSerialTagNumberRequired' | 'deductCore', value: string): boolean {
    const control = this.partsManagementFilterForm.get(controlName);
    const currentValues = control?.value || [];
    return currentValues.includes(value);
  }

  onCheckChange(controlName: 'partActiveStatusesList' | 'isSerialTagNumberRequired' | 'deductCore', value: string) {
    const control = this.partsManagementFilterForm.get(controlName);
    const currentValues = control?.value || [];

    if (currentValues.includes(value)) {
      const updatedValues = currentValues.filter((v: string) => v !== value);
      control?.setValue(updatedValues);
    } else {
      const updatedValues = [...currentValues, value];
      control?.setValue(updatedValues);
    }
  }

  isLoadingOptions: boolean = false;
  async loadAllPartsManagementUniqueOptions() {
    this.isLoadingOptions = true;
    try {
      const [manufacturerCodes, groupCodes] = await Promise.all([
        firstValueFrom(this.partsManagementService.getUniquePartManufacturerCodes()),
        firstValueFrom(this.partsManagementService.getUniquePartGroupCodes()),
      ]);
      this.partManufacturerCodeList = manufacturerCodes;
      this.partGroupCodeList = groupCodes;
    } catch (error) {
      console.error('Error loading unique options:', error);
      this.partManufacturerCodeList = [];
      this.partGroupCodeList = [];
    } finally {
      this.isLoadingOptions = false;
    }
  }

  applyFilters() {
    const partGroupValue = this.partsManagementFilterForm.controls.partGroupCodeList.value;
    const manufacturerValue = this.partsManagementFilterForm.controls.partManufacturerCodeList.value;
    if (partGroupValue && partGroupValue.length > 0) {
      this.partsManagementFilterForm.controls.partGroupCodeList.setValue([...new Set(partGroupValue)]);
    }
    if (manufacturerValue && manufacturerValue.length > 0) {
      this.partsManagementFilterForm.controls.partManufacturerCodeList.setValue([...new Set(manufacturerValue)]);
    }
    this.partsManagementFilterForm.controls.applyPMFilter.setValue(true);
  }

  clearFilters() {
    this.partsManagementFilterForm.reset({
      partCodesList: [],
      partGroupCodeList: [],
      partManufacturerCodeList: [],
      description: [],
      partActiveStatusesList: [],
      isSerialTagNumberRequired: [],
      deductCore: [],
      applyPMFilter: null,
    });
  }
}
